import { default as BaseStack } from '@unifyapps/ui/_components/Stack';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import type { StackBlockStateType, StackComponentType } from '@unifyapps/defs/blocks/Stack/types';
import { getBlockKey } from '@unifyapps/carbon/no-code/utils/block';
import { clsx } from 'clsx';
import { memo } from 'react';
import { getBlockPositionDataAttributes } from '@unifyapps/carbon/no-code/utils/getBlockPositionDataAttributes';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import { getDirection } from './utils';

export type StackProps = BlockComponentProps<
  StackComponentType,
  StackBlockStateType,
  StackBlockStateType,
  HTMLElement
>;

function Stack({
  blockState,
  attributesAndEventListeners,
  computedBlockState,
  registry,
  dndRef,
  block,
}: StackProps) {
  const { content, appearance } = computedBlockState;

  const { className, style } = useBlockAppearanceStyles({
    appearanceStyles: appearance?.styles,
  });

  const { finalStyle } = useCustomStyles({
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- required
    customCSS: block?.additional?.customCSS,
    style,
  });

  const BlockRenderer = registry.getBlockRenderer();
  const wrapClassName = appearance?.wrapContent ? 'flex-wrap' : 'flex-nowrap';

  return (
    <BaseStack
      alignItems={appearance?.alignItems}
      className={clsx(className, wrapClassName)}
      direction={getDirection(appearance)}
      justifyContent={appearance?.justifyContent}
      ref={dndRef}
      style={finalStyle}
      {...attributesAndEventListeners}
    >
      {content.blockIds?.map((blockId, index) => (
        <BlockRenderer
          blockId={blockId}
          dataAttributes={getBlockPositionDataAttributes(index)}
          key={getBlockKey(blockId, index)}
          parentBlockId={blockState.id}
        />
      ))}
    </BaseStack>
  );
}

export default memo(Stack);
